import type { Department, PaneKind, SourceDescriptor } from './contracts';

/**
 * Static catalog of every dashboard pane and its data source.
 *
 * This is the single source of truth for "where does this number come from",
 * used both by the backend (to build panes) and by the docs generator
 * (`scripts/generate-data-sources.ts`). The runtime overlays the live/mock mode
 * and the actual value; everything else lives here so it travels with the code.
 */
export interface PaneDescriptor {
  id: string;
  department: Department;
  title: string;
  kind: PaneKind;
  /** What the pane shows, in one line (used in docs). */
  description: string;
  /** Source descriptor without the runtime `mode` flag. */
  source: Omit<SourceDescriptor, 'mode'>;
}

const SO_SALE_DOC = 'https://docs.superoffice.com/en/api/reference/restful/agent/Sale_Agent/index.html';
const SO_TARGETS_DOC = 'https://docs.superoffice.com/en/api/reference/restful/agent/Targets_Agent/index.html';
const SO_ARCHIVE_DOC = 'https://docs.superoffice.com/en/api/reference/restful/agent/Archive_Agent/index.html';
const GA_DOC = 'https://developers.google.com/analytics/devguides/reporting/data/v1';
const AC_DOC = 'https://developers.activecampaign.com/reference/list-all-contacts';

export const PANE_CATALOG: readonly PaneDescriptor[] = [
  // ---------------- SALG ----------------
  {
    id: 'sales.latest',
    department: 'sales',
    title: 'Seneste nye salg – top 5',
    kind: 'list',
    description: 'De fem seneste vundne salg.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · Salg (sale)',
      detail:
        "SaleAgent / arkivudbyder 'sale': status = 2 (vundet), sorteret efter sale.saledate faldende, top 5. Maintenance/Legacy (saleType 16/17) ekskluderes for kun at vise reelle nye salg.",
      docUrl: SO_SALE_DOC,
      caveats: [
        "!'Nyt salg' = vundet (status 2) vs. registreret — bekræftes med Nordcad.",
        '!Eksklusion af auto-genererede fornyelses-/maintenance-salg bekræftes.',
      ],
    },
  },
  {
    id: 'sales.newCustomers',
    department: 'sales',
    title: 'Nye kunder',
    kind: 'metric',
    description: 'Antal nye virksomheder oprettet i perioden (standard: indeværende måned; følger periodevælgeren).',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · Virksomhed (contact)',
      detail:
        "Arkivudbyder 'contact': contact.registeredDate i perioden. Headline = antal nye virksomheder; fordeling pr. branche (business-feltet) med drill pr. segment.",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        "!'Kunde' = enhver ny contact vs. gated på kategori/relation (kundeportalen bruger reldef_id=14) — bekræftes.",
      ],
    },
  },
  // budgetRemaining (1 col) sits right after newCustomers (1 col) so the first sales
  // row tiles the 4-column grid exactly: latest (2) + newCustomers (1) + budget (1).
  {
    id: 'sales.budgetRemaining',
    department: 'sales',
    title: 'Rest for at nå budget',
    kind: 'progress',
    description: 'Resterende beløb for at nå budgettet (standard: årsmålet; følger periodevælgeren måned/kvartal/år). Drill viser firmaets realisering og — kun for den indloggede bruger — eget mål med udviklingsgraf.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · Targets (budget)',
      detail:
        'Mål fra Targets_Agent (GetTargetGroupForYear: Values pr. periode for valgt scope) vs. realiseret beregnet fra salgs-arkivet (vundet i perioden). Rest = max(0, mål − realiseret). Periodevælgeren vælger mål-udsnittet (måned/kvartal/år); personligt udsnit for den indloggede bruger via associate-match (kun egne tal vises — aldrig kollegers). Konfigureres via SO_TARGETS_JSON.',
      docUrl: SO_TARGETS_DOC,
      caveats: [
        'Kræver et aktivt mål for året i SuperOffice Targets-modulet; findes der intet mål, viser ruden "ingen data" (aldrig demo).',
        'Scope (alle/firma) og standardperiode sættes i SO_TARGETS_JSON; standard = årligt mål for alle. Personlige tal kræver at brugerens Entra-email matcher en SuperOffice-associate med et mål.',
      ],
    },
  },
  {
    id: 'sales.bigDeals',
    department: 'sales',
    title: 'Større deals vi arbejder på',
    kind: 'ranking',
    description: 'Åbne salg over en beløbstærskel, sorteret efter værdi.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · Salg (sale)',
      detail:
        "Arkivudbyder 'sale': status = 1 (åben), amount >= tærskel, sorteret efter sale.amount faldende. saleType 16/17 ekskluderes.",
      docUrl: SO_SALE_DOC,
      caveats: ['!Beløbstærskel for "større" bekræftes (default: top 8 efter beløb).'],
    },
  },

  // ---------------- LEDELSE / ADMIN ----------------
  {
    id: 'admin.forecast',
    department: 'admin',
    title: 'Prognose',
    kind: 'metric',
    description: 'Forventet landing for perioden: vægtet åben pipeline + vundet-til-dato.',
    source: {
      system: 'computed',
      label: 'Beregnet · SuperOffice salgs-arkiv',
      detail:
        'Vægtet åben pipeline (sale.amount × sandsynlighed for status=1) + vundet-til-dato (status=2), beregnet direkte fra SuperOffice salgs-arkivet. Sandsynligheden udledes af stage-navnet (fx "Quote - 30%"), da arkivet ignorerer probability-kolonnen. Drill viser de åbne salg bag pipelinen. Kræver ikke Targets-modulet.',
      docUrl: SO_SALE_DOC,
      caveats: [
        '!Prognoseformel + periode aftales med Nordcad (vægtet pipeline vs. run-rate vs. mål-gap).',
        'Sandsynligheden parses fra stage-labelens procent (verificeret: tenantens arkiv ignorerer probability-kolonnen); stages uden procent vægter 0.',
      ],
    },
  },

  {
    id: 'admin.myAgenda',
    department: 'admin',
    title: 'Din agenda i dag',
    kind: 'agenda',
    description: 'Den indloggede brugers aktiviteter fra nu og frem i dag.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice / Outlook · din kalender',
      detail:
        'Den indloggede brugers aftaler fra nu og frem i dag. Primært SuperOffice (associate matchet på Entra-email); falder tilbage til Outlook (Microsoft Graph) for samme bruger, hvis emailen ikke kan matches til en SuperOffice-associate.',
      docUrl: 'https://docs.superoffice.com/en/api/reference/restful/agent/Appointment_Agent/index.html',
      caveats: [
        'Live kræver kobling af Entra-bruger → SuperOffice-associate (email), og Outlook-fallback kræver delegated Microsoft Graph Calendars.Read på samme Entra-app.',
      ],
    },
  },
  {
    id: 'admin.board',
    department: 'admin',
    title: 'Opslagstavle',
    kind: 'agenda',
    description: 'Kommende aftaler for den valgte SuperOffice-medarbejder.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · Aftaler (appointment)',
      detail:
        "AppointmentAgent / arkivudbyder 'appointment': aftaler for en bestemt associate (SO_BULLETIN_ASSOCIATE_ID) fra i dag og frem, sorteret efter dato/tid.",
      docUrl: 'https://docs.superoffice.com/en/api/reference/restful/agent/Appointment_Agent/index.html',
      caveats: ['Vælg medarbejder via SO_BULLETIN_ASSOCIATE_ID i .env.'],
    },
  },

  // ---------------- MARKETING ----------------
  {
    id: 'marketing.sql',
    department: 'marketing',
    title: 'Totale SQL (pr. måned)',
    kind: 'metric',
    description: 'Sales Qualified Leads pr. måned (én ticket pr. lead).',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · sagskategori Sales/Leads/MQL',
      detail:
        "Tickets i kategori 'Sales/Leads/MQL' oprettet pr. måned (standard: seneste 6 måneder; en valgt periode viser de måneder perioden dækker). Kategori-id slås op via MDO-listen 'ejcategory'.",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        "Kunden kalder panelet 'SQL'; kategorien hedder 'Sales/Leads/MQL' (terminologi bekræftet).",
        'Kategorien findes i produktion, men ikke i testmiljøet (bekræftet juni 2026) — panelet viser 0 i test, rigtige tal i produktion.',
      ],
    },
  },
  {
    id: 'marketing.trials',
    department: 'marketing',
    title: 'OrCAD trials (nye pr. måned)',
    kind: 'metric',
    description: 'Nye OrCAD-trial-forespørgsler pr. måned (én ticket pr. trial).',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · sagskategori Sales/Leads/Trial request/OrCAD X',
      detail:
        "Tickets i kategori 'Sales/Leads/Trial request/OrCAD X' oprettet pr. måned (standard: seneste 6 måneder; en valgt periode viser de måneder perioden dækker).",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        'Tælles fra tickets — IKKE person-feltet x_cdn_pox_trial (et andet begreb i CRM).',
        'Kategoristi bekræftet i tenant: Sales/Leads/Trial request/OrCAD X (bemærk lille "request"; bekræftet juni 2026).',
      ],
    },
  },
  {
    id: 'marketing.subscribers',
    department: 'marketing',
    title: 'Nyhedsbrev-vækst (nye pr. måned)',
    kind: 'metric',
    description: 'Nye abonnenter pr. måned pr. liste (stablet) + samlet vækst.',
    source: {
      system: 'activecampaign',
      label: 'ActiveCampaign · contactLists',
      detail:
        'Nye listemedlemskaber pr. måned pr. liste (abonneringsdato sdate, status=tilmeldt), stablet med samlet total (standard: seneste 6 måneder; en valgt periode viser de måneder perioden dækker). AC kan ikke filtrere contactLists på sdate, så alle medlemskaber hentes og grupperes klient-side (cachet). Lister ekskluderes via AC_EXCLUDE_LIST_IDS.',
      docUrl: AC_DOC,
      caveats: [
        'Tælles pr. abonneringsdato (sdate), ikke kontaktens oprettelsesdato.',
        'Alle lister vises som standard; ekskludér støj (fx engangs-imports) via AC_EXCLUDE_LIST_IDS.',
        'En kontakt på flere lister tæller i hver liste; totalen er summen af stablen (listemedlemskaber, ikke unikke kontakter).',
      ],
    },
  },
  {
    id: 'marketing.sessions',
    department: 'marketing',
    title: 'Hjemmesidetrafik (sessioner pr. måned)',
    kind: 'metric',
    description: 'Totale website-sessioner pr. måned.',
    source: {
      system: 'google-analytics',
      label: 'Google Analytics 4 · sessions',
      detail: "GA4 Data API runReport: metric 'sessions', dimension 'yearMonth' (standard: seneste 6 måneder; en valgt periode viser de måneder perioden dækker).",
      docUrl: GA_DOC,
      caveats: ['Kræver en GA4 service-account med læseadgang til property-id\'et.'],
    },
  },
  {
    id: 'marketing.sessionsCountry',
    department: 'marketing',
    title: 'Sessioner pr. land',
    kind: 'metric',
    description: 'Hjemmesidesessioner fordelt på lande — klik for at se byer pr. land.',
    source: {
      system: 'google-analytics',
      label: 'Google Analytics 4 · sessions pr. land/by',
      detail:
        "GA4 Data API runReport: metric 'sessions', dimension 'country' (alle lande; top 8 i diagrammet + samlet 'Øvrige'-slice der driller til resten) og 'country'+'city' (drill til byer pr. land, med 'Øvrige byer'-rest så byerne stemmer med landets total; skulle GA rapportere flere by-sessioner end landets total, vises rækkerne uændret).",
      docUrl: GA_DOC,
      caveats: [
        'Kræver en GA4 service-account med læseadgang til property-id\'et.',
        'Land- og by-navne kommer fra GA4 (engelske navne).',
        'Diagrammet viser de 8 største lande; drill ned for byer pr. land.',
      ],
    },
  },

  // ---------------- SUPPORT ----------------
  // Order matters for the grid: the wide ranking first, then the two 1-column cards
  // (Antal sager + Rating score) side by side so no orphan gap opens up.
  {
    id: 'support.topCustomers',
    department: 'support',
    title: 'Kunder med flest sager',
    kind: 'ranking',
    description: 'Virksomheder der opretter flest supportsager i tiden.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · tickets grupperet pr. firma',
      detail:
        'Tickets oprettet i perioden (standard: seneste 30 dage; følger periodevælgeren), grupperet pr. firma (kolonnen contact/name), top N. Aggregeres i backend (arkivet har ingen GROUP BY).',
      docUrl: SO_ARCHIVE_DOC,
      caveats: ['!Vindue (30 dage) og top-N bekræftes.'],
    },
  },
  {
    id: 'support.casesLastWeek',
    department: 'support',
    title: 'Antal sager',
    kind: 'metric',
    description: 'Antal supportsager oprettet i perioden (standard: sidste ISO-uge; følger periodevælgeren).',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · tickets',
      detail:
        "Arkivudbyder 'ticket': ticket.createdDate i perioden (standard: sidste ISO-uge, man–søn; følger periodevælgeren). Headline = antal sager; fordeling pr. øverste sagskategori (categoryId opslået i ejcategory) med drill pr. kategori.",
      docUrl: SO_ARCHIVE_DOC,
      caveats: ["!'Sidste uge' = oprettet vs. sidst ændret — bekræftes."],
    },
  },
  {
    id: 'support.rating',
    department: 'support',
    title: 'Rating score',
    kind: 'metric',
    description: 'Gennemsnitlig kundetilfredshed på afsluttede sager.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · ticket-felt x_rating_score',
      detail:
        'Gennemsnit af ticket-feltet x_rating_score hvor x_rating_score > 0 og x_rating_submitted_at i perioden.',
      docUrl: SO_ARCHIVE_DOC,
      caveats: ['Standardperiode for gennemsnittet er seneste 30 dage; følger periodevælgeren.'],
    },
  },
  {
    id: 'support.ratingLeaders',
    department: 'support',
    title: 'Rating · top medarbejdere',
    kind: 'ranking',
    description: 'Top 5 medarbejdere med højeste gennemsnitlige rating i perioden (standard: seneste 30 dage, som Rating score; følger periodevælgeren).',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · ticket-felter x_rating_score + ownedBy',
      detail:
        "Vurderede tickets (x_rating_score > 0, x_rating_submitted_at i perioden) grupperet pr. ejer (stabilt ownedBy/personId; navnet vises), gennemsnit pr. medarbejder, top 5. Foto hentes via dashboardets egen proxy (/api/persons/{id}/image → SuperOffice v1/Person/{id}/Image); mangler fotoet vises initialer. Drill viser medarbejderens vurderede sager.",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        'Få vurderinger giver støjende gennemsnit — antal vurderinger vises pr. medarbejder.',
        'Medarbejdere uden vurderinger i perioden optræder ikke på listen.',
      ],
    },
  },
  // Grid: portalActivity (ranking, 2 kolonner) + portalLogins (1) + portalAdoption (1)
  // fills a full 4-column row below the first support row.
  {
    id: 'support.portalActivity',
    department: 'support',
    title: 'Kundeportal · flest logins',
    kind: 'ranking',
    description: 'Virksomheder med flest logins i kundeportalen i perioden.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · ekstratabel y_portal_event',
      detail:
        "Arkivudbyderen 'dynamic' over ekstratabellen y_portal_event (skrives af kundeportalen ved hvert login): x_event_type = 'login' og x_occurred_at i perioden (standard: seneste 30 dage; følger periodevælgeren), grupperet pr. firma via relationsfeltet x_contact_id. Drill viser hvert login (person, firma, metode, tidspunkt) med dybt link til personen. Aggregeres i backend.",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        'Historik starter ved kundeportalens lancering af login-log (aug. 2026) — ældre perioder er tomme.',
        'Logins registreres best-effort: fejler skrivningen i portalen, mangler rækken her.',
      ],
    },
  },
  {
    id: 'support.portalLogins',
    department: 'support',
    title: 'Kundeportal · logins',
    kind: 'metric',
    description: 'Antal logins i kundeportalen i perioden, med fordeling pr. login-metode.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · ekstratabel y_portal_event',
      detail:
        "Arkivudbyderen 'dynamic' over y_portal_event: antal rækker med x_event_type = 'login' i perioden (standard: seneste 30 dage; følger periodevælgeren). Delta sammenligner den foregående periode af samme længde; fordeling pr. login-metode (x_detail: adgangskode/magic link). Trend pr. måned vises i 'Kundeportal · logins pr. måned'.",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        'Historik starter ved kundeportalens lancering af login-log (aug. 2026) — deltaet er tomt/lavt indtil der er en fuld foregående periode.',
        'Session-forlængelser tæller ikke som logins (kun nye logins skrives af portalen).',
      ],
    },
  },
  {
    id: 'support.portalAdoption',
    department: 'support',
    title: 'Kundeportal · onboarding',
    kind: 'metric',
    description: 'Personer med kundeportal-adgang og andelen der har logget ind de seneste 12 måneder.',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · personfelt Customer portal access',
      detail:
        "Antal personer med udef-feltet 'Customer portal access' (prog-id Nordcad:CustomerPortal) = sand, talt server-side ($inlineCount). Aktive = unikke personer med ≥1 portal-login (y_portal_event) de seneste 12 måneder, som FORTSAT har portal-adgang (fratagne brugere tælles ikke — andelen kan aldrig overstige 100 %); noten viser andelen. Vinduet er fast — panelet følger bevidst IKKE periodevælgeren (onboarding er en beholdning, ikke en strøm).",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        'Feltet er sat i bulk i SuperOffice — tallet er "har adgang", ikke "har aktiveret sin konto".',
        'Aktiv-andelen kan først stige i takt med at login-loggen får historik (startet aug. 2026).',
      ],
    },
  },
  {
    id: 'support.portalLoginsMonthly',
    department: 'support',
    title: 'Kundeportal · logins pr. måned',
    kind: 'metric',
    description: 'Logins i kundeportalen pr. måned — viser om brugen stiger (standard: seneste 6 måneder; en valgt periode viser de måneder perioden dækker).',
    source: {
      system: 'superoffice',
      label: 'SuperOffice · ekstratabel y_portal_event',
      detail:
        "Arkivudbyderen 'dynamic' over y_portal_event: rækker med x_event_type = 'login' grupperet pr. kalendermåned (standard: seneste 6 måneder; en valgt periode viser de måneder perioden dækker). Headline = seneste måned med delta mod måneden før. Drill viser månedens logins (person, firma, metode, tidspunkt).",
      docUrl: SO_ARCHIVE_DOC,
      caveats: [
        'Historik starter ved kundeportalens lancering af login-log (aug. 2026) — tidligere måneder viser 0.',
      ],
    },
  },
] as const;

export function paneById(id: string): PaneDescriptor | undefined {
  return PANE_CATALOG.find((p) => p.id === id);
}
